import React from 'react';
import {
  UserIcon,
  ChatBubbleLeftIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  UserPlusIcon,
  UserMinusIcon
} from '@heroicons/react/24/outline';

/**
 * ActivityItem Component
 *
 * Renders a single activity item in the timeline.
 * Supports different activity types with appropriate icons and formatting.
 */
const ActivityItem = ({ activity, isLast }) => {
  // Format timestamp to relative time
  const formatTimestamp = (timestamp) => {
    try {
      const date = new Date(timestamp);
      const now = new Date();
      const diffInMinutes = Math.floor((now - date) / (1000 * 60));

      if (diffInMinutes < 1) return 'Just now';
      if (diffInMinutes < 60) return `${diffInMinutes}m ago`;

      const diffInHours = Math.floor(diffInMinutes / 60);
      if (diffInHours < 24) return `${diffInHours}h ago`;

      const diffInDays = Math.floor(diffInHours / 24);
      if (diffInDays < 7) return `${diffInDays}d ago`;

      return date.toLocaleDateString();
    } catch (error) {
      return 'Unknown time';
    }
  };

  // Get icon and color based on activity type
  const getActivityIcon = (type) => {
    const iconClass = 'h-5 w-5';

    switch (type) {
      case 'comment':
        return <ChatBubbleLeftIcon className={`${iconClass} text-primary-600`} />;
      case 'assigned':
        return <UserPlusIcon className={`${iconClass} text-accent-600`} />;
      case 'unassigned':
        return <UserMinusIcon className={`${iconClass} text-warning-600`} />;
      case 'status_change':
        return <ArrowPathIcon className={`${iconClass} text-secondary-600`} />;
      case 'resolved':
        return <CheckCircleIcon className={`${iconClass} text-accent-600`} />;
      case 'reopened':
        return <XCircleIcon className={`${iconClass} text-danger-600`} />;
      case 'severity_change':
        return <ExclamationTriangleIcon className={`${iconClass} text-warning-600`} />;
      case 'created':
        return <ExclamationTriangleIcon className={`${iconClass} text-primary-600`} />;
      default:
        return <ArrowPathIcon className={`${iconClass} text-secondary-600`} />;
    }
  };

  const getActivityColor = (type) => {
    switch (type) {
      case 'comment':
        return 'bg-primary-100';
      case 'assigned':
        return 'bg-accent-100';
      case 'unassigned':
        return 'bg-warning-100';
      case 'resolved':
        return 'bg-accent-100';
      case 'reopened':
        return 'bg-danger-100';
      case 'severity_change':
        return 'bg-warning-100';
      case 'created':
        return 'bg-primary-100';
      default:
        return 'bg-secondary-100';
    }
  };

  // Format activity message based on type
  const getActivityMessage = (activity) => {
    const userName = activity.user
      ? `${activity.user.first_name || ''} ${activity.user.last_name || ''}`.trim() || activity.user.email
      : 'Someone';

    switch (activity.activity_type) {
      case 'comment':
        return `${userName} commented`;
      case 'assigned':
        if (activity.field_changed === 'assigned_to') {
          return (
            <>
              {userName} assigned this issue to <span className="font-semibold">{activity.new_value}</span>
            </>
          );
        } else if (activity.field_changed === 'assigned_team') {
          return (
            <>
              {userName} assigned this issue to team <span className="font-semibold">{activity.new_value}</span>
            </>
          );
        }
        return `${userName} assigned this issue`;
      case 'unassigned':
        if (activity.field_changed === 'assigned_to') {
          return (
            <>
              {userName} unassigned <span className="font-semibold">{activity.old_value}</span> from this issue
            </>
          );
        } else if (activity.field_changed === 'assigned_team') {
          return (
            <>
              {userName} unassigned team <span className="font-semibold">{activity.old_value}</span> from this issue
            </>
          );
        }
        return `${userName} unassigned this issue`;
      case 'status_change':
        return (
          <>
            {userName} changed status from <span className="font-semibold">{activity.old_value}</span> to{' '}
            <span className="font-semibold">{activity.new_value}</span>
          </>
        );
      case 'resolved':
        return `${userName} marked this issue as resolved`;
      case 'reopened':
        return `${userName} reopened this issue`;
      case 'severity_change':
        return (
          <>
            {userName} changed severity from <span className="font-semibold">{activity.old_value}</span> to{' '}
            <span className="font-semibold">{activity.new_value}</span>
          </>
        );
      case 'created':
        return `${userName} created this issue`;
      default:
        if (activity.field_changed) {
          return (
            <>
              {userName} changed <span className="font-semibold">{activity.field_changed}</span>
              {activity.old_value && activity.new_value && (
                <>
                  {' '}from <span className="font-semibold">{activity.old_value}</span> to{' '}
                  <span className="font-semibold">{activity.new_value}</span>
                </>
              )}
            </>
          );
        }
        return `${userName} updated this issue`;
    }
  };

  // Get user avatar
  const getUserAvatar = () => {
    if (!activity.user) return null;

    if (activity.user.avatar_url) {
      return (
        <img
          src={activity.user.avatar_url}
          alt=""
          className="h-8 w-8 rounded-full"
        />
      );
    }

    return (
      <div className="h-8 w-8 rounded-full bg-secondary-200 flex items-center justify-center">
        <UserIcon className="h-5 w-5 text-secondary-600" />
      </div>
    );
  };

  return (
    <div className="px-6 py-4">
      <div className="flex items-start space-x-3">
        {/* Timeline Icon */}
        <div className="flex flex-col items-center">
          <div className={`flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center ${getActivityColor(activity.activity_type)}`}>
            {getActivityIcon(activity.activity_type)}
          </div>
          {!isLast && (
            <div className="w-px h-full bg-secondary-200 mt-2" style={{ minHeight: '20px' }}></div>
          )}
        </div>

        {/* Activity Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-2">
              {getUserAvatar()}
              <div>
                <p className="text-sm text-secondary-900">
                  {getActivityMessage(activity)}
                </p>
                <p className="text-xs text-secondary-500 mt-1">
                  {formatTimestamp(activity.created_at)}
                </p>
              </div>
            </div>
          </div>

          {/* Additional content for specific activity types */}
          {activity.activity_type === 'comment' && activity.content && (
            <div className="mt-2 pl-10 text-sm text-secondary-700 bg-secondary-50 p-3 rounded-md">
              {activity.content}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ActivityItem;
